import { useState, useContext, useMemo, useEffect, useRef } from 'react';
import { MenuTab, MenuTabs } from './MenuTabs/MenuTabs';
import { NewDropdown, Typography } from 'app/components/ui';
import { Taxonomy } from './taxonomy/Taxonomy';
import { AllTags } from './allTags/AllTags';
import { TagInfo } from './tagInfo/TagInfo';
import { ExcludePanel } from './ExcludePanel';
import { FootnotePanel } from './FootnotePanel';
import { DetailsModal, DetailsTagConcept } from './Modal/DetailsModal';
import { DeleteTagModal } from './Modal/DeleteTagModal';
import { i18n } from 'app/utils/i18n';
import { useSlate } from 'slate-react';
import { TagWithMetadata, TaxonomyChild } from 'app/api/tag-queries';
import { useShadowSelectionMarked } from './useShadowSelectionMarked';
import { useTagsByDocument } from 'app/api/tag-queries/tags';
import { useDocumentTaxonomyConfig } from 'app/api/tag-queries';
import { useTaxonomyLanguages } from 'app/api/tag-queries/taxonomy';
import { useEditorSelector } from 'app/state/contexts/EditorContext';
import { SidebarContext } from 'app/state/contexts/SidebarContext';
import { TagContext } from 'app/state/contexts/TagContext';
import { NewExtensionModal } from '../../subtoolbar/tags/NewExtension/NewExtensionModal/NewExtensionModal';
import { useDeleteTagWithConfirmation } from './useDeleteTag';
import LanguageIcon from 'app/components/LanguageIcon';
import { getTagLabel } from 'app/utils/tag-utils';
import { useSelector } from 'react-redux';

const isTaxonomyChild = (sel: TaxonomyChild | TagWithMetadata): sel is TaxonomyChild => 'children' in sel;

export const Tags = () => {
  const { activeTagsTab, setActiveTagsTab } = useContext(SidebarContext);
  const { clickedTagIds, setClickedTagIds, tagLanguage, setTagLanguage, selectedFootnoteId, setSelectedFootnoteId } = useContext(TagContext);
  const [selection, setSelection] = useState<TaxonomyChild | TagWithMetadata | null>(null);
  const editor = useSlate();
  const isContentMarked = useShadowSelectionMarked();
  const documentPermissions = useSelector((state: any) => state.document.documentPermissions) || [];
  const canEditTags = documentPermissions.includes('TAG_EDITOR') || documentPermissions.includes('TAG_ADMIN');

  const { pDocument } = useEditorSelector((editor: any) => editor);
  const { data: tagsResponse } = useTagsByDocument(pDocument?.superId, { include: ['concept', 'metadata', 'unit', 'context'] });
  const documentTags = tagsResponse?.tags;
  const { data: taxonomyConfig } = useDocumentTaxonomyConfig(pDocument?.superId);
  const activeTaxonomyIdForExtensions =
    taxonomyConfig?.taxonomySelections?.find((t) => t.isActive)?.taxonomyId ?? taxonomyConfig?.taxonomySelections?.[0]?.taxonomyId;

  const { data: availableLanguages } = useTaxonomyLanguages(pDocument?.superId, activeTaxonomyIdForExtensions);
  const languageOptions = useMemo(() => {
    const langs = availableLanguages?.length ? availableLanguages : ['sv', 'en'];
    return langs.map((lang) => ({
      label: i18n(`lang.${lang}`),
      value: lang,
      icon: <LanguageIcon language={lang} />,
    }));
  }, [availableLanguages]);

  useEffect(() => {
    if (availableLanguages?.length && !availableLanguages.includes(tagLanguage)) {
      setTagLanguage(availableLanguages[0]);
    }
  }, [availableLanguages, tagLanguage, setTagLanguage]);

  // Unified active tab state for better control over tab switching logic
  const activeTab = activeTagsTab as 'taxonomy' | 'allTags' | 'tagInfo';

  // Derive typed selections from the unified state
  const selectedTaxonomyTag = selection && isTaxonomyChild(selection) ? selection : null;
  const selectedDocTagId = selection && !isTaxonomyChild(selection) ? selection.id : null;

  // Derive selectedDocTag from the query cache so it's always fresh after mutations
  const selectedDocTag = useMemo(() => {
    if (!selectedDocTagId || !documentTags) return null;
    return documentTags.find((t) => t.id === selectedDocTagId) ?? null;
  }, [selectedDocTagId, documentTags]);

  // When a taxonomy tag is selected that's used in the document, resolve the matching doc tag
  const effectiveDocTag = useMemo(() => {
    if (selectedDocTag) return selectedDocTag;
    if (!selectedTaxonomyTag || !documentTags) return null;
    return documentTags.find((t) => t.concept?.name === selectedTaxonomyTag.id) ?? null;
  }, [selectedDocTag, selectedTaxonomyTag, documentTags]);

  // Compute usedTags from real data: unique concept names from documentTags
  const usedTags = useMemo(() => {
    if (!documentTags) return [];
    const conceptNames = documentTags.map((tag) => tag.concept?.name).filter((name): name is string => !!name);
    return Array.from(new Set(conceptNames));
  }, [documentTags]);

  // React to tag clicks from the editor
  const [filterTagIds, setFilterTagIds] = useState<string[] | null>(null);

  useEffect(() => {
    if (!clickedTagIds || clickedTagIds.length === 0) return;
    if (!documentTags) return; // wait for documentTags to load before switching tab

    const liveIds = clickedTagIds.filter((id: string) => documentTags.some((t) => t.id === id));
    if (liveIds.length === 0) return;

    if (liveIds.length === 1) {
      const docTag = documentTags.find((t) => t.id === liveIds[0]) ?? null;
      setSelection(docTag);
      setFilterTagIds(null);
      setSelectedFootnoteId(null);
      setActiveTagsTab('tagInfo');
    } else {
      setSelection(null);
      setFilterTagIds(liveIds);
      setActiveTagsTab('allTags');
    }

    setClickedTagIds(null);
  }, [clickedTagIds, documentTags, setActiveTagsTab, setClickedTagIds, setSelectedFootnoteId]);

  useEffect(() => {
    if (selectedFootnoteId) setActiveTagsTab('tagInfo');
  }, [selectedFootnoteId, setActiveTagsTab]);

  const [modalState, setModalState] = useState<{
    show: boolean;
    tagConcept: DetailsTagConcept | null;
  }>({ show: false, tagConcept: null });
  const [isExtensionModalOpen, setIsExtensionModalOpen] = useState(false);
  const [extensionWiderAnchorSeed, setExtensionWiderAnchorSeed] = useState<string | null>(null);

  // Delete tag flow
  const {
    showModal: showDeleteModal,
    isDeleting,
    requestDelete,
    requestDeleteById,
    confirmDelete,
    cancelDelete,
  } = useDeleteTagWithConfirmation({
    editor,
    documentSuperId: pDocument?.superId,
    onDeleted: () => {
      setSelection(null);
      if (activeTab === 'tagInfo') {
        setActiveTagsTab('allTags');
      }
    },
  });

  // Expose requestDeleteById for the Slate plugin via a ref on the editor
  const deleteTagRef = useRef<((tagId: string) => void) | null>(null);
  deleteTagRef.current = requestDeleteById;
  useEffect(() => {
    (editor as any).__onTagAboutToBeEmpty = (tagId: string) => {
      deleteTagRef.current?.(tagId);
    };
  }, [editor]);

  const tabs: MenuTab[] = [
    { key: 'taxonomy', label: i18n('taxonomy.title') },
    { key: 'allTags', label: i18n('tag.tags.label') },
    { key: 'tagInfo', label: i18n('tag.info.title') },
  ];

  const onDetailsClick = (tag: TaxonomyChild | TagWithMetadata) => {
    if ('conceptType' in tag) {
      setModalState({
        show: true,
        tagConcept: {
          name: tag.id,
          conceptType: tag.conceptType,
          label: getTagLabel({ languages: tag.labels, currentLang: tagLanguage }),
        },
      });
    } else {
      setModalState({
        show: true,
        tagConcept: {
          id: tag.id,
          name: tag.concept?.name ?? '',
          conceptType: tag.concept?.conceptType,
          label: getTagLabel({ languages: tag.concept?.labels ?? {}, currentLang: tagLanguage }),
          notes: tag?.notes ?? undefined,
        },
      });
    }
  };

  const onCreateExtension = (tagIdOrConceptName: string) => {
    // Called from two places with different arg shapes:
    //   - `TagListItem` passes a tag UUID (we resolve to the tag's concept name)
    //   - `TaxonomyFooter` "extend from concept" passes `selectedTag.id`,
    //     which is already a concept QName (e.g. `ifrs-full:Revenue`)
    // Fall through to treating the arg as a concept name when no tag matches.
    const tag = documentTags?.find((t) => t.id === tagIdOrConceptName);
    setExtensionWiderAnchorSeed(tag?.concept?.name ?? tagIdOrConceptName);
    setIsExtensionModalOpen(true);
  };

  return (
    <div className="w-100 pt-2 bg-white h-100 d-flex flex-column">
      <div
        className="d-flex flex-row px-3"
        style={{ alignItems: 'center', justifyContent: 'space-between', gap: '8px', paddingBottom: '12px', paddingTop: '8px' }}
      >
        <Typography variant="body-7" fontWeight="bold">
          {i18n('tag.tagging')}
        </Typography>

        <NewDropdown currentValue={tagLanguage} options={languageOptions} onUpdate={(value: string) => setTagLanguage(value)} />
      </div>

      <MenuTabs tabs={tabs} activeTab={activeTab} onTabChange={(key) => setActiveTagsTab(key as typeof activeTab)} />

      <ExcludePanel />

      {/* Content based on activeTab */}

      {activeTab === 'taxonomy' && (
        <Taxonomy
          selectedTagId={selectedTaxonomyTag?.id ?? effectiveDocTag?.concept?.name ?? null}
          isContentMarked={canEditTags ? isContentMarked : false}
          usedTags={usedTags}
          documentTags={documentTags ?? []}
          onSelectionChange={(tag) => setSelection(tag)}
          onDetailsClick={onDetailsClick}
          onExtensionClick={canEditTags ? onCreateExtension : undefined}
          onHiddenTagCreated={(tag: any) => {
            setSelection(tag);
            setSelectedFootnoteId(null);
            setActiveTagsTab('tagInfo');
          }}
        />
      )}
      {activeTab === 'allTags' && (
        <AllTags
          selectedTag={effectiveDocTag}
          onSelectTag={(tag) => {
            setSelectedFootnoteId(null);
            setSelection(tag && effectiveDocTag?.id === tag.id ? null : tag ?? null);
          }}
          onTagDetailsClick={onDetailsClick}
          onExtensionClick={canEditTags ? onCreateExtension : undefined}
          onDeleteClick={canEditTags ? requestDelete : undefined}
          filterTagIds={filterTagIds}
        />
      )}
      {activeTab === 'tagInfo' &&
        (selectedFootnoteId ? (
          <FootnotePanel />
        ) : (
          <TagInfo
            selectedConceptName={selectedTaxonomyTag?.id ?? null}
            selectedTag={effectiveDocTag}
            onDeleteClick={canEditTags ? requestDelete : undefined}
            onSelectTag={(tag) => setSelection(tag)}
            isContentMarked={canEditTags ? isContentMarked : false}
            documentTags={documentTags ?? []}
            readOnly={!canEditTags}
            onCancel={() => {
              setSelection(null);
              setActiveTagsTab('allTags');
            }}
          />
        ))}

      <DetailsModal show={modalState.show} tagConcept={modalState.tagConcept} onClose={() => setModalState({ show: false, tagConcept: null })} />

      {pDocument?.superId && activeTaxonomyIdForExtensions && (
        <NewExtensionModal
          show={isExtensionModalOpen}
          onClose={() => {
            setIsExtensionModalOpen(false);
            setExtensionWiderAnchorSeed(null);
          }}
          documentSuperId={pDocument.superId}
          taxonomyId={activeTaxonomyIdForExtensions}
          widerAnchorSeed={extensionWiderAnchorSeed}
        />
      )}

      <DeleteTagModal show={showDeleteModal} onConfirm={confirmDelete} onCancel={cancelDelete} busy={isDeleting} />
    </div>
  );
};
